import React, { useState, useEffect, useRef, useCallback } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import IframeModal from '../../components/IframeModal'
import { fetchPersonalizedStops, setPersonalizationStatus } from 'aria-viewer/tour/tourActions'
import { PersonalizationStatus } from 'aria-viewer/tour/constants/PersonalizationStatus'
import withRetry from 'aria-viewer/utils/retry'
import axios from 'axios'
import { getEnv } from 'aria-viewer/utils/environment'

const BASE_URL = getEnv('REACT_APP_BASE_URL')

/**
 * IframeModalContainer
 */
const IframeModalContainer = props => {
    const [isVisible, setIsVisible] = useState(true)
    const [showCloseButton, setShowCloseButton] = useState(!props.delayCloseButtonUntilOnReady)
    const iframeRef = useRef()
    const closeButtonRef = useRef()
    const onIframeLoad = useCallback(_ => setIsVisible(true), [])
    const onComplete = useCallback(millisecondsUntilClose => {
        props.onComplete && props.onComplete(millisecondsUntilClose)
    }, [])
    const onPersonalizationComplete = () => {
        props.setPersonalizationStatus(PersonalizationStatus.IN_PROGRESS)
        const { inst_id: instId, loc_id: locId, tourid, userkey } = props.uriParams
        withRetry({
            retries: 6,
            delay: 500,
            backOffCoefficient: 2,
            fn: _ => {
                return new Promise((resolve, reject) => {
                    axios
                        .get(
                            `${BASE_URL}/api/v2/institutions/${instId}/locations/${locId}/personalized-stops/me/status`,
                            { params: { userkey, tour_id: tourid } }
                        )
                        .then(response => {
                            const { live_updates: liveUpdates } = response.data.resources
                            const completedUpdate = liveUpdates.some(step => step.completed_ranking === true)
                            if (completedUpdate) {
                                resolve()
                            } else {
                                reject()
                            }
                        })
                        .catch(error => {
                            reject(error)
                        })
                })
            },
            onSuccess: _ => {
                props.fetchPersonalizedStops(instId, locId, tourid, userkey)
            },
            onFailed: _ => {
                props.setPersonalizationStatus(PersonalizationStatus.ERROR)
            },
        })
    }
    //If we are delaying our close button display, lets make sure we show it eventually..
    const readyDelayTimeout = props.delayCloseButtonUntilOnReady
        ? setTimeout(() => {
              setShowCloseButton(true)
          }, 5000)
        : false
    const onReady = props.delayCloseButtonUntilOnReady
        ? useCallback(_ => {
              clearTimeout(readyDelayTimeout)
              setTimeout(() => {
                  setShowCloseButton(true)
              }, 1000)
              props.onReady && props.onReady()
          }, [])
        : props.onReady

    useEffect(() => {
        let hasFocusedonButton = true
        const onKeyDown = event => {
            if (event.keyCode == 9) {
                event.preventDefault()
                if (hasFocusedonButton) {
                    iframeRef && iframeRef.current.focus()
                    //accessibility controller adds keyboard-focus
                    closeButtonRef && closeButtonRef.current.classList.remove('keyboard-focused')
                    hasFocusedonButton = false
                } else {
                    closeButtonRef && closeButtonRef.current.focus()
                    hasFocusedonButton = true
                }
            }
        }

        document.addEventListener('keydown', onKeyDown)

        return () => {
            document.removeEventListener('keydown', onKeyDown)
        }
    }, [])

    return (
        isVisible && (
            <IframeModal
                {...props}
                onIframeLoad={onIframeLoad}
                onComplete={onComplete}
                onReady={onReady}
                showCloseButton={showCloseButton}
                iframeRef={iframeRef}
                closeButtonRef={closeButtonRef}
                onPersonalizationComplete={onPersonalizationComplete}
                // onReceiveMessage={data => {}}
            />
        )
    )
}

IframeModalContainer.defaultProps = {
    src: null,
    uriParams: null,
    //is our iframe visible?
    isVisible: true,
    delayCloseButtonUntilOnReady: false,
}

IframeModalContainer.propTypes = {
    src: PropTypes.string,
    uriParams: PropTypes.object,
}

export default connect(null, { fetchPersonalizedStops, setPersonalizationStatus })(IframeModalContainer)
